/**
 * Field Favorites Context.
 *
 * Mirrors GettyFavoritesContext: loads favorites on mount, exposes
 * optimistic add/remove with rollback on error, and re-fetches when the
 * active catalog owner changes (delegation support).
 */

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  addFieldFavorite as addFieldFavoriteApi,
  FieldFavoritesResponse,
  FieldFavoriteType,
  getFieldFavorites,
  removeFieldFavorite as removeFieldFavoriteApi,
} from '../utils/fieldFavoritesApi';
import { useAuth } from '../hooks/useAuth';
import { ActiveCatalogContext } from './ActiveCatalogContext';
import { logger } from '../utils/logger';

interface FieldFavoritesContextValue {
  favorites: FieldFavoritesResponse;
  loading: boolean;
  error: string | null;
  isFavorite: (favoriteType: FieldFavoriteType, value: string) => boolean;
  addFavorite: (favoriteType: FieldFavoriteType, value: string) => Promise<void>;
  removeFavorite: (favoriteType: FieldFavoriteType, value: string) => Promise<void>;
  getFavoritesForType: (favoriteType: FieldFavoriteType) => string[];
}

const defaultFavorites: FieldFavoritesResponse = {
  awards: [],
  exhibitions: [],
  publications: [],
  locations: [],
  provenance: [],
  updatedAt: null,
};

// eslint-disable-next-line react-refresh/only-export-components
export const FieldFavoritesContext = createContext<FieldFavoritesContextValue | undefined>(undefined);

export const FieldFavoritesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const activeCatalogContext = useContext(ActiveCatalogContext);
  const activeCatalogOwnerId = activeCatalogContext?.activeCatalogOwnerId ?? null;
  const [favorites, setFavorites] = useState<FieldFavoritesResponse>(defaultFavorites);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Skip overwriting optimistic state with stale server data (mirrors #980 fix).
  const pendingToggleCount = useRef(0);

  useEffect(() => {
    if (!isAuthenticated) {
      setFavorites(defaultFavorites);
      return;
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getFieldFavorites(activeCatalogOwnerId ?? undefined);
        if (pendingToggleCount.current === 0) {
          // Spread over defaults so a response missing a newer type key
          // (frontend/backend deploy skew) can't leave a list undefined.
          setFavorites({ ...defaultFavorites, ...data });
        }
      } catch (err) {
        setError('Failed to load favorites');
        logger.error('Failed to load field favorites', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [isAuthenticated, activeCatalogOwnerId]);

  const isFavorite = useCallback(
    (favoriteType: FieldFavoriteType, value: string): boolean => {
      return favorites[favoriteType].includes(value);
    },
    [favorites]
  );

  const addFavorite = useCallback(
    async (favoriteType: FieldFavoriteType, value: string) => {
      const trimmed = value.trim();
      if (!trimmed || isFavorite(favoriteType, trimmed)) {
        return;
      }

      const preOptimisticList = favorites[favoriteType];

      setFavorites((prev) => ({
        ...prev,
        [favoriteType]: [...prev[favoriteType], trimmed],
      }));

      pendingToggleCount.current += 1;
      try {
        const result = await addFieldFavoriteApi(favoriteType, trimmed, activeCatalogOwnerId ?? undefined);
        setFavorites({ ...defaultFavorites, ...result });
      } catch (err) {
        setFavorites((prev) => ({ ...prev, [favoriteType]: preOptimisticList }));
        setError('Failed to add favorite');
        logger.error('Failed to add field favorite', err);
      } finally {
        pendingToggleCount.current -= 1;
      }
    },
    [favorites, isFavorite, activeCatalogOwnerId]
  );

  const removeFavorite = useCallback(
    async (favoriteType: FieldFavoriteType, value: string) => {
      const preOptimisticList = favorites[favoriteType];

      setFavorites((prev) => ({
        ...prev,
        [favoriteType]: prev[favoriteType].filter((v) => v !== value),
      }));

      pendingToggleCount.current += 1;
      try {
        const result = await removeFieldFavoriteApi(favoriteType, value, activeCatalogOwnerId ?? undefined);
        setFavorites({ ...defaultFavorites, ...result });
      } catch (err) {
        setFavorites((prev) => ({ ...prev, [favoriteType]: preOptimisticList }));
        setError('Failed to remove favorite');
        logger.error('Failed to remove field favorite', err);
      } finally {
        pendingToggleCount.current -= 1;
      }
    },
    [favorites, activeCatalogOwnerId]
  );

  const getFavoritesForType = useCallback(
    (favoriteType: FieldFavoriteType): string[] => favorites[favoriteType],
    [favorites]
  );

  return (
    <FieldFavoritesContext.Provider
      value={{
        favorites,
        loading,
        error,
        isFavorite,
        addFavorite,
        removeFavorite,
        getFavoritesForType,
      }}
    >
      {children}
    </FieldFavoritesContext.Provider>
  );
};
